import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaRegWindowMinimize } from 'react-icons/fa';
import { TbHeadphones } from 'react-icons/tb';
import CenterModalWindow from '../../layout/ReusableComponent/CenterModalWindow/CenterModalWindow';
import ConfirmModal from '../../layout/ReusableComponent/ConfirmModal';
import AudioPlayerMiniDock from '../../layout/ReusableComponent/audioPlayer/AudioPlayerMiniDock';
import AudioPlayerPlaylistPanel from '../../layout/ReusableComponent/audioPlayer/AudioPlayerPlaylistPanel';
import StorageBoxAudioBrowser from '../../layout/ReusableComponent/audioPlayer/StorageBoxAudioBrowser';
import StorageBoxAudioPlayer from '../../layout/ReusableComponent/audioPlayer/StorageBoxAudioPlayer';
import StorageBoxAudioStudioLayout, {
  useCoarseMobileLayout,
} from '../../layout/ReusableComponent/audioPlayer/StorageBoxAudioStudioLayout';
import { useAppShellHeaderOffset } from '../../layout/ReusableComponent/audioPlayer/useAppShellHeaderOffset';
import { useAudioPlaylist } from '../../layout/ReusableComponent/audioPlayer/useAudioPlaylist';
import { isPreviewableAudio } from '../api/storageBoxApi';

export function isStorageBoxAudioItem(item) {
  return item?.type === 'file' && isPreviewableAudio(item.mimeType, item.name);
}

export default function StorageBoxAudioPlayerModal({ isOpen, onClose, item, publicToken, queueApiRef }) {
  const show = isOpen && isStorageBoxAudioItem(item);
  const mobileLayout = useCoarseMobileLayout();

  const playlist = useAudioPlaylist(item, { enabled: show });

  const prevSeedItemIdRef = useRef(null);

  useEffect(() => {
    if (!show || !item?.itemId) {
      prevSeedItemIdRef.current = null;
      return;
    }
    if (prevSeedItemIdRef.current !== item.itemId) {
      playlist.playItemNow(item);
    }
    prevSeedItemIdRef.current = item.itemId;
  }, [show, item?.itemId, playlist.playItemNow]);

  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [mobilePane, setMobilePane] = useState('player');
  const [dockPlaying, setDockPlaying] = useState(false);
  const [equalizerOpen, setEqualizerOpen] = useState(false);
  const playerApiRef = useRef(null);

  useAppShellHeaderOffset(show && mobileLayout && !minimized);

  useEffect(() => {
    if (!show) {
      setCloseConfirmOpen(false);
      setMinimized(false);
      setMobilePane('player');
    }
  }, [show]);

  useEffect(() => {
    if (!show || !minimized) {
      document.body.classList.remove('sb-audio-player-dock-open');
      return undefined;
    }
    document.body.classList.add('sb-audio-player-dock-open');
    return () => document.body.classList.remove('sb-audio-player-dock-open');
  }, [show, minimized]);

  const handleMinimize = useCallback(() => {
    setMinimized(true);
  }, []);

  const handleExpand = useCallback(() => {
    setMinimized(false);
  }, []);

  useEffect(() => {
    if (!show || !queueApiRef) return undefined;
    queueApiRef.current = {
      queuePlayNext: playlist.queueTrackPlayNext,
      playItemNow: playlist.playItemNow,
    };
    return () => {
      queueApiRef.current = null;
    };
  }, [show, queueApiRef, playlist.queueTrackPlayNext, playlist.playItemNow]);

  const requestClose = useCallback(() => {
    if (closeConfirmOpen) return;
    setCloseConfirmOpen(true);
  }, [closeConfirmOpen]);

  const cancelClose = useCallback(() => {
    setCloseConfirmOpen(false);
  }, []);

  const confirmClose = useCallback(() => {
    setCloseConfirmOpen(false);
    onClose();
  }, [onClose]);

  const overlayClassName = useMemo(
    () =>
      [
        'sb-audio-player-modal-overlay',
        mobileLayout && !minimized ? 'sb-audio-player-modal-overlay--sheet' : '',
        minimized ? 'sb-audio-player-modal-overlay--minimized' : '',
      ]
        .filter(Boolean)
        .join(' '),
    [mobileLayout, minimized]
  );

  const contentClassName = useMemo(
    () =>
      [
        'sb-audio-player-modal',
        mobileLayout && !minimized ? 'sb-audio-player-modal--sheet' : '',
        mobileLayout && !minimized ? `sb-audio-player-modal--pane-${mobilePane}` : '',
        minimized ? 'sb-audio-player-modal--minimized' : '',
        !mobileLayout && !minimized ? 'sb-audio-player-modal--studio' : '',
      ]
        .filter(Boolean)
        .join(' '),
    [mobileLayout, minimized, mobilePane]
  );

  const contentStyle = useMemo(() => {
    if (minimized) {
      return {
        width: '100%',
        maxWidth: '100%',
        height: 'auto',
        maxHeight: 'none',
        minHeight: 0,
        margin: 0,
        borderRadius: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      };
    }
    if (mobileLayout) {
      return {
        width: '100%',
        maxWidth: '100%',
        height: 'calc(100dvh - var(--app-audio-sheet-top, 0px))',
        maxHeight: 'calc(100dvh - var(--app-audio-sheet-top, 0px))',
        minHeight: 0,
        margin: 0,
        borderRadius: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      };
    }
    return {
      width: 'min(1180px, calc(100vw - 40px))',
      height: 'min(86dvh, 760px)',
      maxHeight: 'min(86dvh, 760px)',
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      borderRadius: '22px',
    };
  }, [mobileLayout, minimized]);

  const trackTitle = playlist.currentStorageItem?.name || item?.name || 'Audio';

  const [sleepDockLine, setSleepDockLine] = useState('');

  useEffect(() => {
    if (!show || !minimized) {
      setSleepDockLine('');
      return undefined;
    }
    const tick = () => {
      const api = playerApiRef.current;
      if (!api?.sleepTimerActive) {
        setSleepDockLine('');
        return;
      }
      const label = api.sleepTimerRemainingLabel;
      if (label === 'End of song' || label === 'End of track') {
        setSleepDockLine('Sleep: end of song');
      } else if (label === 'End of queue') {
        setSleepDockLine('Sleep: end of queue');
      } else if (label) {
        setSleepDockLine(`Sleep in ${label}`);
      } else {
        setSleepDockLine('');
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [show, minimized]);

  const dockMetaLine = useMemo(() => {
    const parts = [];
    if (sleepDockLine) parts.push(sleepDockLine);
    if (playlist.tracks.length > 1) {
      parts.push(`${playlist.tracks.length} tracks in queue`);
    }
    return parts.join(' · ');
  }, [sleepDockLine, playlist.tracks.length]);

  const headerActions = useMemo(() => {
    if (minimized || mobileLayout) return null;
    return (
      <button
        type="button"
        className="sb-audio-player-modal__header-btn audio-player-window-minimize-btn"
        onClick={handleMinimize}
        aria-label="Minimize player"
        title="Minimize"
      >
        <FaRegWindowMinimize aria-hidden />
      </button>
    );
  }, [minimized, mobileLayout, handleMinimize]);

  const handleBrowsePlayNow = useCallback(
    (fileItem) => {
      playlist.playItemNow(fileItem);
      if (mobileLayout) setMobilePane('player');
    },
    [mobileLayout, playlist.playItemNow]
  );

  const playlistPanel = (
    <AudioPlayerPlaylistPanel
      playlistName={playlist.playlistName}
      onPlaylistNameChange={playlist.setPlaylistName}
      tracks={playlist.tracks}
      currentTrackId={playlist.currentTrackId}
      shuffle={playlist.shuffle}
      onShuffleChange={playlist.setShuffle}
      repeat={playlist.repeat}
      onRepeatChange={playlist.setRepeat}
      onAddCurrentTrack={playlist.addCurrentTrack}
      addCurrentLabel="Add playing track"
      onSelectTrack={playlist.selectTrack}
      onRemoveTrack={(trackId) => {
        playlist.setTracks((prev) => {
          const next = prev.filter((t) => t.trackId !== trackId);
          if (playlist.currentTrackId === trackId) {
            playlist.setCurrentTrackId(next[0]?.trackId || '');
          }
          return next;
        });
      }}
      onLoadTracks={(loaded, meta) => playlist.loadTracks(loaded, meta)}
      statusMessage={playlist.status}
      onStatusMessage={playlist.setStatus}
      eqGains={playlist.eqGains}
    />
  );

  const playerNode = (
    <StorageBoxAudioPlayer
      item={playlist.currentStorageItem}
      publicToken={publicToken}
      className={
        minimized
          ? 'audio-player--dock-sr-only'
          : 'audio-player--sb-modal audio-player--sb-modal--storagebox'
      }
      onQueueAdvance={playlist.advanceTrack}
      onPlayStateChange={setDockPlaying}
      playerApiRef={playerApiRef}
      queueHasNextTrack={playlist.hasNextTrackInQueue}
      autoPlayRequest={playlist.autoPlayRequest}
      equalizerGains={playlist.eqGains}
      onEqualizerGainsChange={playlist.setEqGains}
      onEqualizerPanelChange={setEqualizerOpen}
      onWindowMinimize={mobileLayout ? handleMinimize : null}
      onWindowClose={mobileLayout ? requestClose : null}
    />
  );

  const libraryNode = (
    <StorageBoxAudioBrowser
      className="sb-audio-browser--studio"
      onSelectFile={handleBrowsePlayNow}
      onQueuePlayNext={playlist.queueTrackPlayNext}
    />
  );

  return (
    <>
      <ConfirmModal
        isOpen={show && closeConfirmOpen}
        onClose={cancelClose}
        onConfirm={confirmClose}
        title="Close audio player?"
        subtitle={playlist.currentStorageItem?.name || item?.name}
        description="Stop playback and close the player? Your saved playlists on this device are kept—you can reopen this file anytime."
        confirmLabel="Yes"
        cancelLabel="Cancel"
        icon={TbHeadphones}
      />

      <CenterModalWindow
        isOpen={show}
        onClose={requestClose}
        width={mobileLayout ? '100%' : 'min(1180px, calc(100vw - 40px))'}
        height="auto"
        swipeToClose={false}
        overlayClassName={overlayClassName}
        contentClassName={contentClassName}
        contentStyle={contentStyle}
        headerActions={headerActions}
        closeButtonClassName={
          minimized || (mobileLayout && !minimized) ? 'sb-audio-player-modal__close--hidden' : ''
        }
      >
        <div className="sb-audio-player-modal__body">
          {minimized ? (
            <AudioPlayerMiniDock
              title={trackTitle}
              metaLine={dockMetaLine}
              playing={dockPlaying}
              onTogglePlay={() => playerApiRef.current?.togglePlay?.()}
              onRestore={handleExpand}
              onClose={requestClose}
            />
          ) : null}

          {!minimized && mobileLayout ? (
            <div className="sb-audio-player-modal__grabber" aria-hidden />
          ) : null}

          {show && !minimized ? (
            <StorageBoxAudioStudioLayout
              mobileLayout={mobileLayout}
              mobilePane={mobilePane}
              onMobilePaneChange={setMobilePane}
              trackCount={playlist.tracks.length}
              player={playerNode}
              playlistPanel={playlistPanel}
              library={libraryNode}
              onMinimize={mobileLayout ? handleMinimize : null}
              onClose={mobileLayout ? requestClose : null}
              playerEmbedsWindowActions
            />
          ) : null}
        </div>
      </CenterModalWindow>
    </>
  );
}
